import React, { useState, useRef, useEffect, useMemo } from "react";
import ReactDOM from "react-dom";
import { useDepartment } from "../../../stores/departmentStore";
import { api } from "../../../utils/axios-instance";

const HOURS = Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
const MINUTES = Array.from({ length: 12 }, (_, i) =>
  String(i * 5).padStart(2, "0")
);
const DAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];

// Hardcoded local-only department option — never added to departments state or Firestore
const MISC_DEPARTMENT = { name: "Misc", metadata: { bg: "#6F767E" } };

// Renders children into document.body so overflow:hidden on parents doesn't clip them
const Portal = ({ children }) => ReactDOM.createPortal(children, document.body);

// Calculates absolute position for a popover anchored to a ref element
const usePopoverPosition = (ref, open) => {
  const [pos, setPos] = useState({ top: 0, left: 0 });
  useEffect(() => {
    if (!open || !ref.current) return;
    const rect = ref.current.getBoundingClientRect();
    setPos({
      top: rect.top + window.scrollY, // will be adjusted to open upward by the caller
      left: rect.left + window.scrollX,
      width: rect.width,
      bottom: rect.bottom + window.scrollY,
    });
  }, [open, ref]);
  return pos;
};

const TodoWidget = () => {
  const { departments } = useDepartment();

  const [todos, setTodos] = useState([]);
  const [selectedDept, setSelectedDept] = useState(null);
  const [showInlineForm, setShowInlineForm] = useState(false);
  const [form, setForm] = useState({
    title: "",
    date: "",
    hour: "",
    minute: "",
    department: "Misc",
  });
  const [formError, setFormError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const [showDatePicker, setShowDatePicker] = useState(false);
  const [showTimePicker, setShowTimePicker] = useState(false);
  const [showDeptPicker, setShowDeptPicker] = useState(false);
  const [calendarMonth, setCalendarMonth] = useState(() => new Date());

  const listRef = useRef(null);
  const titleInputRef = useRef(null);
  const dateBtnRef = useRef(null);
  const timeBtnRef = useRef(null);
  const deptBtnRef = useRef(null);

  const datePos = usePopoverPosition(dateBtnRef, showDatePicker);
  const timePos = usePopoverPosition(timeBtnRef, showTimePicker);
  const deptPos = usePopoverPosition(deptBtnRef, showDeptPicker);

  useEffect(() => {
    const fetchTodos = async () => {
      setIsLoading(true);
      setLoadError("");
      try {
        const response = await api.get("users/todos/");
        setTodos(response.data?.todoItems || []);
      } catch (error) {
        console.error("Error fetching todos:", error);
        setLoadError("Failed to load todos.");
      } finally {
        setIsLoading(false);
      }
    };
    fetchTodos();
  }, []);

  useEffect(() => {
    if (showInlineForm) {
      if (listRef.current)
        listRef.current.scrollTop = listRef.current.scrollHeight;
      setTimeout(() => titleInputRef.current?.focus(), 50);
      if (selectedDept) setForm((p) => ({ ...p, department: selectedDept }));
    }
  }, [showInlineForm, selectedDept]);

  useEffect(() => {
    const handleClick = (e) => {
      if (dateBtnRef.current && !dateBtnRef.current.contains(e.target)) {
        const calEl = document.getElementById("todo-date-popover");
        if (!calEl || !calEl.contains(e.target)) setShowDatePicker(false);
      }
      if (timeBtnRef.current && !timeBtnRef.current.contains(e.target)) {
        const timeEl = document.getElementById("todo-time-popover");
        if (!timeEl || !timeEl.contains(e.target)) setShowTimePicker(false);
      }
      if (deptBtnRef.current && !deptBtnRef.current.contains(e.target)) {
        const deptEl = document.getElementById("todo-dept-popover");
        if (!deptEl || !deptEl.contains(e.target)) setShowDeptPicker(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const getDeptColor = (deptName) => {
    if (deptName === MISC_DEPARTMENT.name) return MISC_DEPARTMENT.metadata.bg;
    const d = departments.find((dep) => dep.name === deptName);
    return d?.metadata?.bg || "#6366f1";
  };

  const filteredTodos = selectedDept
    ? todos.filter((t) => t.department === selectedDept)
    : todos;

  const handleSave = async () => {
    if (!form.title.trim()) {
      setFormError("Please fill the title before saving.");
      return;
    }
    const combinedTime =
      form.hour && form.minute ? `${form.hour}:${form.minute}` : "";
    setFormError("");
    setIsSaving(true);
    try {
      const response = await api.post("users/todos/add/", {
        title: form.title,
        date: form.date,
        time: combinedTime,
        department: form.department,
      });
      setTodos((prev) => [...prev, response.data.todo]);
      setForm({
        title: "",
        date: "",
        hour: "",
        minute: "",
        department: "Misc",
      });
      setShowInlineForm(false);
    } catch (error) {
      console.error("Error adding todo:", error);
      setFormError("Failed to add todo. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete("users/todos/delete/", { data: { id } });
      setTodos((prev) => prev.filter((t) => t.id !== id));
    } catch (error) {
      console.error("Error deleting todo:", error);
    }
  };

  const handleCancel = () => {
    setForm({ title: "", date: "", hour: "", minute: "", department: "Misc" });
    setFormError("");
    setShowInlineForm(false);
    setShowDatePicker(false);
    setShowTimePicker(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") handleCancel();
  };

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return null;
    const [yyyy, mm, dd] = dateStr.split("-");
    return `${dd}-${mm}-${yyyy}`;
  };

  const formatDateShort = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr + "T00:00:00");
    return d.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
  };

  const monthLabel = useMemo(
    () =>
      calendarMonth.toLocaleDateString("en-IN", {
        month: "long",
        year: "numeric",
      }),
    [calendarMonth]
  );

  const calendarDays = useMemo(() => {
    const year = calendarMonth.getFullYear();
    const month = calendarMonth.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const totalDays = new Date(year, month + 1, 0).getDate();
    const grid = Array(firstDay).fill(null);
    for (let i = 1; i <= totalDays; i++) grid.push(i);
    return grid;
  }, [calendarMonth]);

  const selectDate = (day) => {
    const year = calendarMonth.getFullYear();
    const month = String(calendarMonth.getMonth() + 1).padStart(2, "0");
    const d = String(day).padStart(2, "0");
    setForm((p) => ({ ...p, date: `${year}-${month}-${d}` }));
    setShowDatePicker(false);
  };

  const chipStyle = (isActive, color) => ({
    fontSize: "11px",
    fontWeight: 600,
    padding: "4px 10px",
    borderRadius: "20px",
    border: isActive
      ? `1.5px solid ${color || "#4A3AFF"}`
      : "1.5px solid #E5E7EB",
    background: isActive ? (color ? color + "22" : "#EEF2FF") : "#F9FAFB",
    color: isActive ? color || "#4A3AFF" : "#6F767E",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: 6,
    whiteSpace: "nowrap",
  });

  const POPOVER_BASE = {
    position: "absolute",
    background: "#fff",
    border: "1px solid #E5E7EB",
    borderRadius: 12,
    boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
    zIndex: 99999,
  };

  return (
    <div
      style={{
        background: "#ffffff",
        borderRadius: "12px",
        border: "1px solid #EBEBEB",
        boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
        display: "flex",
        flexDirection: "column",
        height: "400px",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <div
        style={{
          flexShrink: 0,
          padding: "16px 20px 12px",
          borderBottom: "1px solid #F3F4F6",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: "10px",
          }}
        >
          <span
            style={{
              fontSize: "14px",
              fontWeight: 700,
              color: "#1A1D1F",
              letterSpacing: "-0.01em",
            }}
          >
            TODO
          </span>
          <button
            onClick={() => setShowInlineForm(true)}
            style={{
              width: 28,
              height: 28,
              borderRadius: "6px",
              border: "1px solid #EBEBEB",
              background: "#F9FAFB",
              color: "#4A3AFF",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "18px",
              fontWeight: 600,
              lineHeight: 1,
            }}
          >
            +
          </button>
        </div>

        <div
          style={{
            display: "flex",
            gap: "6px",
            overflowX: "auto",
            paddingBottom: "2px",
            scrollbarWidth: "none",
          }}
        >
          <button
            onClick={() => setSelectedDept(null)}
            style={{
              flexShrink: 0,
              fontSize: "11px",
              fontWeight: 600,
              padding: "3px 10px",
              borderRadius: "20px",
              border:
                selectedDept === null
                  ? "2px solid #4A3AFF"
                  : "1.5px solid #E5E7EB",
              background: selectedDept === null ? "#EEF0FF" : "#F9FAFB",
              color: selectedDept === null ? "#4A3AFF" : "#6F767E",
              cursor: "pointer",
              transition: "all 0.15s",
            }}
          >
            All
          </button>
          {departments.map((dept) => {
            const color = dept?.metadata?.bg || "#6366f1";
            const isActive = selectedDept === dept.name;
            return (
              <button
                key={dept.id}
                onClick={() => setSelectedDept(isActive ? null : dept.name)}
                style={{
                  flexShrink: 0,
                  fontSize: "11px",
                  fontWeight: 600,
                  padding: "3px 10px",
                  borderRadius: "20px",
                  border: isActive
                    ? `2px solid ${color}`
                    : "1.5px solid #E5E7EB",
                  background: isActive ? color + "22" : "#F9FAFB",
                  color: isActive ? color : "#6F767E",
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                {dept.name.replace(/_/g, " ")}
              </button>
            );
          })}
        </div>
      </div>

      {/* List */}
      <div
        ref={listRef}
        style={{
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          padding: "6px 0",
          scrollbarWidth: "thin",
          scrollbarColor: "#E5E7EB transparent",
        }}
      >
        {isLoading ? (
          <div
            style={{
              padding: "20px",
              textAlign: "center",
              color: "#9CA3AF",
              fontSize: "13px",
            }}
          >
            Loading todos...
          </div>
        ) : loadError ? (
          <div
            style={{
              padding: "20px",
              textAlign: "center",
              color: "#D1293D",
              fontSize: "13px",
            }}
          >
            {loadError}
          </div>
        ) : filteredTodos.length === 0 && !showInlineForm ? (
          <div
            style={{
              padding: "20px",
              textAlign: "center",
              color: "#9CA3AF",
              fontSize: "13px",
            }}
          >
            No todos for this department.
          </div>
        ) : (
          filteredTodos.map((todo) => {
            const color = getDeptColor(todo.department);
            return (
              <div
                key={todo.id}
                style={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "10px",
                  padding: "9px 20px",
                  borderBottom: "1px solid #F9FAFB",
                  transition: "background 0.12s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.background = "#FAFAFA")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.background = "transparent")
                }
              >
                <div
                  style={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    background: color,
                    flexShrink: 0,
                    marginTop: 5,
                  }}
                />
                <div style={{ flex: 1 }}>
                  <p
                    style={{
                      fontSize: "13px",
                      color: "#374151",
                      margin: 0,
                      fontWeight: 500,
                      lineHeight: 1.4,
                    }}
                  >
                    {todo.title}
                  </p>
                  <p
                    style={{
                      fontSize: "11px",
                      color: "#9CA3AF",
                      margin: "3px 0 0",
                    }}
                  >
                    {todo.department}
                    {todo.date && ` · ${formatDateShort(todo.date)}`}
                    {todo.time && ` · ${todo.time}`}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => handleDelete(todo.id)}
                  style={{
                    flexShrink: 0,
                    border: "none",
                    background: "none",
                    color: "#D1D5DB",
                    cursor: "pointer",
                    fontSize: "13px",
                    padding: "2px 4px",
                    lineHeight: 1,
                    marginTop: 3,
                    opacity: 0.7,
                    transition: "color 0.12s, opacity 0.12s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = "#D1293D";
                    e.currentTarget.style.opacity = 1;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = "#D1D5DB";
                    e.currentTarget.style.opacity = 0.7;
                  }}
                >
                  ✕
                </button>
              </div>
            );
          })
        )}

        {/* Inline form */}
        {showInlineForm && (
          <div
            style={{
              padding: "10px 20px 12px",
              borderTop: "1px solid #F3F4F6",
            }}
          >
            <div
              style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}
            >
              <div
                style={{
                  width: 16,
                  height: 16,
                  borderRadius: "50%",
                  border: "2px solid #D1D5DB",
                  flexShrink: 0,
                  marginTop: 3,
                }}
              />
              <div style={{ flex: 1 }}>
                <input
                  ref={titleInputRef}
                  placeholder="New todo..."
                  value={form.title}
                  onChange={(e) =>
                    setForm((p) => ({ ...p, title: e.target.value }))
                  }
                  onKeyDown={handleKeyDown}
                  style={{
                    width: "100%",
                    border: "none",
                    outline: "none",
                    background: "transparent",
                    fontSize: "13px",
                    color: "#1A1D1F",
                    fontWeight: 500,
                    padding: 0,
                    boxSizing: "border-box",
                  }}
                />

                <div
                  style={{
                    display: "flex",
                    gap: "6px",
                    marginTop: "8px",
                    flexWrap: "wrap",
                    alignItems: "center",
                  }}
                >
                  {/* DATE BUTTON */}
                  <button
                    ref={dateBtnRef}
                    type="button"
                    onClick={() => {
                      setShowDatePicker((v) => !v);
                      setShowTimePicker(false);
                      setShowDeptPicker(false);
                    }}
                    style={chipStyle(!!form.date)}
                  >
                    📅{" "}
                    {form.date ? formatDateDisplay(form.date) : "Select Date"}
                  </button>

                  {/* TIME BUTTON */}
                  <button
                    ref={timeBtnRef}
                    type="button"
                    onClick={() => {
                      setShowTimePicker((v) => !v);
                      setShowDatePicker(false);
                      setShowDeptPicker(false);
                    }}
                    style={chipStyle(!!(form.hour && form.minute))}
                  >
                    🕒{" "}
                    {form.hour && form.minute
                      ? `${form.hour}:${form.minute}`
                      : "Select Time"}
                  </button>

                  {/* DEPARTMENT */}
                  {!selectedDept ? (
                    (() => {
                      const deptColor =
                        form.department === MISC_DEPARTMENT.name
                          ? MISC_DEPARTMENT.metadata.bg
                          : departments.find((d) => d.name === form.department)
                              ?.metadata?.bg || null;
                      return (
                        <button
                          ref={deptBtnRef}
                          type="button"
                          onClick={() => {
                            setShowDeptPicker((v) => !v);
                            setShowDatePicker(false);
                            setShowTimePicker(false);
                          }}
                          style={chipStyle(!!form.department, deptColor)}
                        >
                          🏢{" "}
                          {form.department
                            ? form.department === MISC_DEPARTMENT.name
                              ? "Misc"
                              : form.department.replace(/_/g, " ")
                            : "Department"}
                        </button>
                      );
                    })()
                  ) : (
                    <div
                      style={{
                        fontSize: "11px",
                        fontWeight: 600,
                        padding: "4px 12px",
                        borderRadius: "20px",
                        border: `1.5px solid ${getDeptColor(selectedDept)}`,
                        background: getDeptColor(selectedDept) + "22",
                        color: getDeptColor(selectedDept),
                        userSelect: "none",
                      }}
                    >
                      🏢 {selectedDept.replace(/_/g, " ")}
                    </div>
                  )}
                </div>

                {formError && (
                  <p
                    style={{
                      fontSize: "11px",
                      color: "#D1293D",
                      margin: "6px 0 0",
                      fontWeight: 500,
                    }}
                  >
                    {formError}
                  </p>
                )}

                <div style={{ display: "flex", gap: "6px", marginTop: "10px" }}>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    style={{
                      fontSize: "11px",
                      fontWeight: 600,
                      padding: "4px 12px",
                      borderRadius: "6px",
                      border: "none",
                      background: "#4A3AFF",
                      color: "#fff",
                      cursor: isSaving ? "default" : "pointer",
                      opacity: isSaving ? 0.7 : 1,
                    }}
                  >
                    {isSaving ? "Adding..." : "Add"}
                  </button>
                  <button
                    onClick={handleCancel}
                    style={{
                      fontSize: "11px",
                      fontWeight: 600,
                      padding: "4px 12px",
                      borderRadius: "6px",
                      border: "1px solid #E5E7EB",
                      background: "transparent",
                      color: "#6F767E",
                      cursor: "pointer",
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* DATE POPOVER — portalled to body */}
      {showDatePicker && datePos.bottom && (
        <Portal>
          <div
            id="todo-date-popover"
            style={{
              ...POPOVER_BASE,
              padding: 14,
              width: 240,
              top: datePos.top - 235, // sits just above the button
              left: datePos.left,
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: 10,
              }}
            >
              <button
                type="button"
                onClick={() =>
                  setCalendarMonth((m) => {
                    const n = new Date(m);
                    n.setMonth(n.getMonth() - 1);
                    return n;
                  })
                }
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: 16,
                  color: "#6F767E",
                }}
              >
                ←
              </button>
              <span style={{ fontSize: 12, fontWeight: 700, color: "#1A1D1F" }}>
                {monthLabel}
              </span>
              <button
                type="button"
                onClick={() =>
                  setCalendarMonth((m) => {
                    const n = new Date(m);
                    n.setMonth(n.getMonth() + 1);
                    return n;
                  })
                }
                style={{
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  fontSize: 16,
                  color: "#6F767E",
                }}
              >
                →
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7,1fr)",
                textAlign: "center",
                fontSize: 10,
                color: "#9CA3AF",
                fontWeight: 600,
                marginBottom: 4,
              }}
            >
              {DAY_LABELS.map((d, i) => (
                <div key={i}>{d}</div>
              ))}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(7,1fr)",
                gap: 2,
              }}
            >
              {calendarDays.map((day, i) => {
                if (!day) return <div key={i} />;
                const yyyy = calendarMonth.getFullYear();
                const mm = String(calendarMonth.getMonth() + 1).padStart(
                  2,
                  "0"
                );
                const dd = String(day).padStart(2, "0");
                const isSelected = form.date === `${yyyy}-${mm}-${dd}`;
                return (
                  <button
                    key={day}
                    type="button"
                    onClick={() => selectDate(day)}
                    style={{
                      width: 28,
                      height: 28,
                      borderRadius: 6,
                      border: "none",
                      cursor: "pointer",
                      fontSize: 11,
                      fontWeight: 600,
                      background: isSelected ? "#4A3AFF" : "transparent",
                      color: isSelected ? "#fff" : "#374151",
                    }}
                  >
                    {day}
                  </button>
                );
              })}
            </div>
          </div>
        </Portal>
      )}

      {/* TIME POPOVER — portalled to body */}
      {showTimePicker && timePos.bottom && (
        <Portal>
          <style>{`#todo-time-popover div::-webkit-scrollbar { display: none; }`}</style>
          <div
            id="todo-time-popover"
            style={{
              ...POPOVER_BASE,
              padding: "10px 14px",
              top: timePos.top - 210, // 180px picker height + 30px gap
              left: timePos.left,
              display: "flex",
              gap: 0,
            }}
          >
            {/* Hours column */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                maxHeight: 180,
                overflowY: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#9CA3AF",
                  textAlign: "center",
                  padding: "0 0 4px",
                }}
              >
                HH
              </div>
              {HOURS.map((h) => (
                <button
                  key={h}
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, hour: h }))}
                  style={{
                    fontSize: "13px",
                    fontWeight: 600,
                    padding: "5px 14px",
                    borderRadius: "6px",
                    border: "none",
                    background: form.hour === h ? "#4A3AFF" : "transparent",
                    color: form.hour === h ? "#fff" : "#374151",
                    cursor: "pointer",
                    textAlign: "center",
                    minWidth: 48,
                  }}
                >
                  {h}
                </button>
              ))}
            </div>

            <div
              style={{
                width: 1,
                background: "#E5E7EB",
                margin: "0 8px",
                alignSelf: "stretch",
              }}
            />

            {/* Minutes column */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: 2,
                maxHeight: 180,
                overflowY: "auto",
                scrollbarWidth: "none",
                msOverflowStyle: "none",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <div
                style={{
                  fontSize: 10,
                  fontWeight: 700,
                  color: "#9CA3AF",
                  textAlign: "center",
                  padding: "0 0 4px",
                }}
              >
                MM
              </div>
              {MINUTES.map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, minute: m }))}
                  style={{
                    fontSize: "13px",
                    fontWeight: 600,
                    padding: "5px 14px",
                    borderRadius: "6px",
                    border: "none",
                    background: form.minute === m ? "#4A3AFF" : "transparent",
                    color: form.minute === m ? "#fff" : "#374151",
                    cursor: "pointer",
                    textAlign: "center",
                    minWidth: 48,
                  }}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>
        </Portal>
      )}

      {/* DEPARTMENT POPOVER — portalled to body */}
      {showDeptPicker && deptPos.bottom && (
        <Portal>
          <div
            id="todo-dept-popover"
            style={{
              ...POPOVER_BASE,
              padding: 6,
              width: Math.max(deptPos.width || 140, 140),
              top:
                deptPos.top -
                Math.min((departments.length + 1) * 32 + 20, 230),
              left: deptPos.left,
              display: "flex",
              flexDirection: "column",
              gap: 2,
              maxHeight: 220,
              overflowY: "auto",
            }}
          >
            <button
              key={MISC_DEPARTMENT.name}
              type="button"
              onClick={() => {
                setFormError("");
                setForm((p) => ({ ...p, department: MISC_DEPARTMENT.name }));
                setShowDeptPicker(false);
              }}
              style={{
                fontSize: 12,
                fontWeight: 600,
                padding: "6px 10px",
                borderRadius: 6,
                border: "none",
                textAlign: "left",
                cursor: "pointer",
                background:
                  form.department === MISC_DEPARTMENT.name
                    ? MISC_DEPARTMENT.metadata.bg + "22"
                    : "transparent",
                color:
                  form.department === MISC_DEPARTMENT.name
                    ? MISC_DEPARTMENT.metadata.bg
                    : "#374151",
              }}
            >
              Misc
            </button>
            {departments.map((dept) => {
              const color = dept?.metadata?.bg || "#6366f1";
              const isSelected = form.department === dept.name;
              return (
                <button
                  key={dept.id}
                  type="button"
                  onClick={() => {
                    setFormError("");
                    setForm((p) => ({ ...p, department: dept.name }));
                    setShowDeptPicker(false);
                  }}
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    padding: "6px 10px",
                    borderRadius: 6,
                    border: "none",
                    textAlign: "left",
                    cursor: "pointer",
                    background: isSelected ? color + "22" : "transparent",
                    color: isSelected ? color : "#374151",
                  }}
                >
                  {dept.name.replace(/_/g, " ")}
                </button>
              );
            })}
          </div>
        </Portal>
      )}
    </div>
  );
};

export default TodoWidget;
